/**
 * V4WorkflowSwitcher — Phase 5c-1.
 *
 * Header-mounted dropdown that lists every (non-archived) workflow on
 * tenant and lets the operator jump between them without leaving V4.
 *
 * Before 5c, V4 had no way to switch workflows from inside the canvas —
 * operators had to bounce back to /canvas (V3) to pick a different one
 * or paste a UUID into the URL. The switcher closes that gap: click the
 * workflow name in the header → popover → pick another or create a new
 * one.
 *
 * Composition:
 *   - Trigger button shows current workflow name + chevron, click toggles popover
 *   - Popover header: search input + "+ New" action
 *   - Body: scrollable list of workflows. Each row shows name + status pill
 *     + last-modified-at + agent label (if linked)
 *   - Footer: "Save current as new…" action (only when a workflow IS loaded
 *     AND the operator has edit rights)
 *
 * Navigation contract:
 *   - Selecting another workflow: host's `onSelect(workflowId)` fires.
 *     The host owns the dirty-state confirm + route change.
 *   - "+ New workflow": host's `onNew()` opens the name dialog.
 *   - "Save current as new…": host's `onSaveAs()` opens the name dialog.
 *
 * Telemetry: the host wires the relevant track calls in its handlers;
 * this component is dumb about analytics so it stays portable.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Plus, Search, Copy as CopyIcon, X, Loader2 } from 'lucide-react';
import * as api from '../../services/api';

const POPOVER_WIDTH = 360;

// Status → tone. Matches V2 Pill semantics so the chip color is
// consistent with how the workflows appear on other pages.
const STATUS_TONE = {
  DRAFT:      { bg: '#fef3c7', fg: '#92400e' },
  ACTIVE:     { bg: '#d1fae5', fg: '#065f46' },
  PROTECTED:  { bg: '#dbeafe', fg: '#1e40af' },
  LOCKED:     { bg: '#e5e7eb', fg: '#475569' },
  ARCHIVED:   { bg: '#f3f4f6', fg: '#6b7280' },
  PUBLISHED:  { bg: '#d1fae5', fg: '#065f46' },
};

function fmtRelative(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '';
  const ms = Date.now() - d.getTime();
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
  if (ms < 30 * 86_400_000) return `${Math.round(ms / 86_400_000)}d ago`;
  return d.toLocaleDateString();
}

export default function V4WorkflowSwitcher({
  currentWorkflowId,
  currentWorkflowName,
  onSelect,
  onNew,
  onSaveAs,
  // When true, hides the "Save current as new" affordance — used when
  // no workflow is loaded (nothing to clone yet).
  canSaveAs = true,
  // CTX-AUTH — host passes the active session's org/tenant so the BE
  // list-workflows call carries the tenant scope. Without these the
  // unscoped call returns HTTP 400 in current BE.
  orgId    = null,
  tenantId = null,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [items, setItems] = useState(null);     /* null = not loaded yet */
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  // Click outside / Escape closes
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (!popoverRef.current) return;
      if (popoverRef.current.contains(e.target)) return;
      if (triggerRef.current && triggerRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Fetch on open. Cache stays for the lifetime of the popover; a
  // re-open re-fetches so freshly-created workflows show up immediately.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.getWorkflows({ orgId, tenantId })
      .then((res) => {
        if (cancelled) return;
        if (res && res.ok === false) {
          setError(res.error || 'Failed to load workflows');
          setItems([]);
          return;
        }
        const list = (res && 'data' in res) ? res.data : res;
        setItems(Array.isArray(list) ? list : []);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message || String(e));
        setItems([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, orgId, tenantId]);

  const filtered = useMemo(() => {
    if (!Array.isArray(items)) return [];
    const q = search.trim().toLowerCase();
    let out = items.slice();
    if (q) {
      out = out.filter((w) => {
        const hay = [w.name, w.description, w.status, w.id].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
      });
    }
    // Sort: most-recently-modified first
    out.sort((a, b) => {
      const at = new Date(a.updatedAt || a.modifiedAt || a.createdAt || 0).getTime();
      const bt = new Date(b.updatedAt || b.modifiedAt || b.createdAt || 0).getTime();
      return bt - at;
    });
    return out;
  }, [items, search]);

  const handleSelect = (id) => {
    setOpen(false);
    if (id === currentWorkflowId) return;
    if (typeof onSelect === 'function') onSelect(id);
  };

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Switch workflow"
        style={triggerStyle}
      >
        <span style={{
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          maxWidth: 260,
        }}>
          {currentWorkflowName || (currentWorkflowId ? 'Loading…' : 'No workflow loaded')}
        </span>
        <ChevronDown size={12} style={{ flexShrink: 0, color: '#64748b' }} />
      </button>

      {open && (
        <div ref={popoverRef} role="listbox" style={popoverStyle}>
          {/* Search + New */}
          <div style={{ padding: '8px 10px', borderBottom: '0.5px solid #e2e8f0', display: 'flex', gap: 6 }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <Search size={11} style={{
                position: 'absolute', left: 8, top: '50%',
                transform: 'translateY(-50%)', color: '#94a3b8',
              }} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search workflows…"
                autoFocus
                style={searchStyle}
              />
            </div>
            <button
              type="button"
              onClick={() => { setOpen(false); if (typeof onNew === 'function') onNew(); }}
              title="Create a new workflow"
              style={smallActionButtonStyle}
            >
              <Plus size={11} /> New
            </button>
          </div>

          {/* List */}
          <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, maxHeight: 360 }}>
            {loading && (
              <div style={emptyStateStyle}>
                <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                <span>Loading workflows…</span>
              </div>
            )}
            {!loading && error && (
              <div style={{ ...emptyStateStyle, color: '#b91c1c' }}>
                <X size={14} />
                <span>{error}</span>
              </div>
            )}
            {!loading && !error && filtered.length === 0 && (
              <div style={emptyStateStyle}>
                {search.trim() !== ''
                  ? <span>No workflows match “{search}”.</span>
                  : <span>No workflows on this tenant yet — click <strong>+ New</strong> above.</span>}
              </div>
            )}
            {!loading && !error && filtered.map((w) => {
              const tone = STATUS_TONE[String(w.status || '').toUpperCase()] || STATUS_TONE.DRAFT;
              const isCurrent = w.id === currentWorkflowId;
              return (
                <button
                  key={w.id}
                  type="button"
                  onClick={() => handleSelect(w.id)}
                  style={{
                    ...rowStyle,
                    background: isCurrent ? '#f1f5f9' : '#ffffff',
                  }}
                  title={w.description || w.name}
                >
                  <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2,
                    }}>
                      <span style={{
                        fontSize: 12, fontWeight: 600, color: '#0f172a',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        flex: 1, minWidth: 0,
                      }}>
                        {w.name || '(unnamed)'}
                      </span>
                      {isCurrent && (
                        <span style={{
                          fontSize: 9, fontWeight: 700, color: '#0f172a',
                          background: '#e2e8f0', padding: '1px 5px', borderRadius: 999,
                          letterSpacing: 0.4, textTransform: 'uppercase',
                        }}>
                          current
                        </span>
                      )}
                    </div>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      fontSize: 10, color: '#64748b',
                    }}>
                      <span style={{
                        padding: '1px 5px', borderRadius: 3,
                        background: tone.bg, color: tone.fg,
                        fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4,
                      }}>
                        {w.status || 'draft'}
                      </span>
                      {(w.updatedAt || w.modifiedAt || w.createdAt) && (
                        <span>{fmtRelative(w.updatedAt || w.modifiedAt || w.createdAt)}</span>
                      )}
                      {w.agentId && (
                        <span style={{
                          padding: '1px 5px', borderRadius: 3,
                          background: '#fef3c7', color: '#92400e',
                        }}>
                          linked
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Footer — Save As */}
          {canSaveAs && currentWorkflowId && (
            <div style={{
              padding: '8px 10px', borderTop: '0.5px solid #e2e8f0',
              display: 'flex', justifyContent: 'flex-end',
            }}>
              <button
                type="button"
                onClick={() => { setOpen(false); if (typeof onSaveAs === 'function') onSaveAs(); }}
                title="Clone the current workflow into a new one"
                style={smallActionButtonStyle}
              >
                <CopyIcon size={11} /> Save current as new…
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const triggerStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '4px 8px',
  fontSize: 14, fontWeight: 600, color: '#0f172a',
  background: 'transparent',
  border: '0.5px solid transparent', borderRadius: 6,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const popoverStyle = {
  position: 'absolute', top: 'calc(100% + 6px)', left: 0,
  width: POPOVER_WIDTH,
  display: 'flex', flexDirection: 'column',
  background: '#ffffff',
  border: '1px solid #e2e8f0', borderRadius: 8,
  boxShadow: '0 12px 28px -8px rgba(15, 23, 42, 0.18), 0 2px 6px -1px rgba(15, 23, 42, 0.08)',
  zIndex: 50,
  fontFamily: 'inherit',
};

const searchStyle = {
  width: '100%', padding: '5px 8px 5px 24px',
  fontSize: 12, color: '#0f172a',
  background: '#ffffff',
  border: '0.5px solid #cbd5e1', borderRadius: 4,
  outline: 'none', fontFamily: 'inherit',
};

const smallActionButtonStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '4px 8px', fontSize: 11, fontWeight: 600,
  color: '#0f172a', background: '#f1f5f9',
  border: '0.5px solid #e2e8f0', borderRadius: 4,
  cursor: 'pointer', whiteSpace: 'nowrap',
  fontFamily: 'inherit',
};

const rowStyle = {
  display: 'flex', alignItems: 'center', gap: 8,
  width: '100%', padding: '8px 10px',
  background: '#ffffff', border: 'none',
  borderBottom: '0.5px solid #f1f5f9',
  cursor: 'pointer', textAlign: 'left',
  fontFamily: 'inherit',
};

const emptyStateStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  padding: 16, fontSize: 11.5, color: '#64748b', textAlign: 'center',
};
